import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  Select,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import { MdEdit, MdVisibility } from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from 'components/error-message';
import moment from 'moment';

// Column Helper
const columnHelper = createColumnHelper();

// TeamTable Component
export default function ServicesTable(props) {
  const { tableData, loading, fetchAgain } = props;
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  // const primaryTextColor = useColorModeValue('brand.600', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [selectedId, setSelectedId] = React.useState('');

  // Columns definition
  const columns = [
    columnHelper.accessor((row, index) => index + 1, {
      id: '#',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          #
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('code', {
      id: 'code',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Code
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('office_code', {
      id: 'office_code',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Office Code
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    // columnHelper.accessor('description', {
    //   id: 'description',
    //   header: () => (
    //     <Text fontSize="12px" color="gray.400" fontWeight="bold">
    //       Description
    //     </Text>
    //   ),
    //   cell: (info) => {
    //     const value = info.getValue();
    //     const truncated =
    //       value && value.length > 50 ? value.substring(0, 50) + '...' : value;

    //     return (
    //       <Text color={textColor} fontSize="sm" fontWeight="700">
    //         {truncated}
    //       </Text>
    //     );
    //   },
    // }),

    columnHelper.accessor('amount_il', {
      id: 'amount_il',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          amount IL
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          ${info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('amount_wi', {
      id: 'amount_wi',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          amount WI
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          ${info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('created_at', {
      id: 'created_at',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Added at
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ACTION
        </Text>
      ),
      cell: (info) => {
        const handleViewClick = () => {
          const itemId = info.row.original.id;
          console.log('Navigating to details with ID:', itemId);

          navigate(`details/${itemId}`, { state: { id: itemId } });
        };

        const handleEditClick = (e) => {
          const itemId = info.row.original.id;
          navigate('edit-service', {
            state: { type: 'edit', serviceId: itemId },
          });
        };

        return (
          <Box display="flex">
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit Details"
              colorScheme="brand"
              size="sm"
              mr="6px"
              onClick={handleEditClick}
            />
            <IconButton
              icon={<MdVisibility style={{ marginLeft: '-1px' }} />}
              aria-label="View Details"
              colorScheme="blackAlpha"
              size="sm"
              onClick={handleViewClick}
            />
          </Box>
        );
      },
    }),
  ];

  const [data, setData] = React.useState([]);

  const table = useReactTable({
    data: data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  React.useEffect(() => {
    if (tableData && tableData.length > 0) {
      setData([...tableData]);
    }
  }, [tableData]);

  const [searchTerm, setSearchTerm] = React.useState('');

  React.useEffect(() => {
    if (!tableData) return;

    let filteredData = [...tableData]; // Ensure you work on a copy

    filteredData = filteredData.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        item.user_name?.trim().toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        item.email?.trim().toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        item.code?.trim().toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        item.office_code?.trim().toLowerCase().includes(searchTerm.trim().toLowerCase());

      return matchesSearch;
    });

    setCurrentPage(1);
    setData(filteredData);
  }, [tableData, searchTerm]);

  const itemsPerPageOptions = [10, 20, 30, 50];
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);

  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const totalPages = Math.ceil(data.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  return (
    <>
      <Card flexDirection="column" w="100%" px="0px">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
          px={{ base: 4, md: 6 }}
          gap={4}
          mb={4}
          flexWrap="wrap"
        >
          {/* Title */}
          <Text
            color={textColor}
            fontSize={{ base: '20px', md: '24px' }}
            fontWeight="700"
            lineHeight="100%"
            mb={{ base: 2, md: 0 }}
          >
            Services
          </Text>

          {/* Search Bar */}
          <Box
            w={{ base: '100%', md: 'auto' }}
            flex={1}
            display="flex"
            justifyContent={{ base: 'center', md: 'flex-end' }}
          // mr={{ md: 5 }}
          >
            <SearchBar
              width="100%"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>

          {/* Buttons */}
          <Flex
            w={{ base: '100%', md: 'auto' }}
            direction={{ base: 'column', sm: 'row' }} // removed !important
            gap={2}
            justify={{ base: 'center', md: 'flex-end' }}
          >
            <Button
              w={{ base: '100%', sm: 'auto' }}
              variant="brand"
              fontWeight="500"
              onClick={() => navigate('add-service', { state: { type: 'add' } })}
              minH={"36px"}
              borderRadius="16px"
              mb={0}
              size="sm"
            >
              Add Service
            </Button>
            <Button
              w={{ base: '100%', sm: 'auto' }}
              colorScheme="green"
              variant="solid"
              fontWeight="500"
              onClick={() => navigate('manage-categories')}
              minH={"36px"}
              borderRadius="16px"
              mb={0}
              size="sm"
            >
              Service Category
            </Button>
            <Button
              w={{ base: '100%', sm: 'auto' }}
              colorScheme="green"
              variant="solid"
              fontWeight="500"
              onClick={() => navigate('manage-treatment-area')}
              minH={"36px"}
              borderRadius="16px"
              mb={0}
              size="sm"
            >
              Treatment Area
            </Button>
          </Flex>

        </Flex>


        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="8px"
          justify="space-between"
          align="center"
          flexWrap="wrap"
          // direction={{ base: 'column', md: 'row' }}
          gap={{ base: 4, md: 0 }}
        >
          <Box w={{ base: '100%', md: 'auto' }}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              width={{ base: '100%', md: 'fit-content' }}
              borderRadius="16px"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </Select>
          </Box>

          <Box
            w={{ base: '100%', md: 'auto' }}
            display="flex"
            justifyContent={{ base: 'center', md: 'flex-end' }}
          >
            <Pagination
              totalPages={totalPages}
              initialPage={currentPage}
              onPageChange={handlePageChange}
            />{' '}
          </Box>
        </Flex>

        <Box overflowX="auto">
          <Table variant="striped" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex justifyContent="space-between" align="center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {paginatedData.map((row, index) => (
                <Tr key={index}>
                  {table
                    .getRowModel()
                    .rows.find((r) => r.original === row) // Ensure correct row mapping
                    ?.getVisibleCells()
                    ?.map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize="14px"
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {!loading &&
          data.length === 0 &&
          (searchTerm.length > 0 ? (
            <ErrorMessage source="results" />
          ) : (
            <ErrorMessage source="services" />
          ))}
      </Card >
    </>
  );
}
